import React, { useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import {
  Buildings,
  TrendUp,
  ChartLineUp,
  UserFocus,
  ArrowRight,
  ShieldCheck,
  CheckCircle,
} from "@phosphor-icons/react";

const HERO = "https://images.pexels.com/photos/7108700/pexels-photo-7108700.jpeg";
const FORMATION_IMG = "https://images.unsplash.com/photo-1493397212122-2b85dda8106b";
const MGMT_IMG = "https://images.pexels.com/photos/34823910/pexels-photo-34823910.jpeg";

const HERO_MOTION = {
  initial: { opacity: 0, y: 24 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.7 },
};
const CARD_INITIAL = { opacity: 0, y: 30 };
const CARD_WHILE_INVIEW = { opacity: 1, y: 0 };
const CARD_VIEWPORT = { once: true };

const services = [
  {
    id: "business-formation",
    name: "Business Formation",
    icon: Buildings,
    price: "$300",
    desc: "Start your business the right way. We collect your details and file the LLC or corporation you choose.",
    points: ["LLC, S-Corp, C-Corp filing", "EIN registration", "Registered agent service"],
  },
  {
    id: "business-credit-builder",
    name: "Business Financial Solutions",
    icon: TrendUp,
    price: "$1,000",
    desc: "Position your business to qualify for funding and growth with a strategy tailored to your goals.",
    points: ["Vendor & funding strategy", "Fundability roadmap", "Financial profile setup"],
  },
  {
    id: "personal-credit-builder",
    name: "Wealth Foundation Program",
    icon: UserFocus,
    price: "$500",
    desc: "Build a strong personal financial foundation with a plan built around your goals.",
    points: ["Financial profile review", "Personalized growth plan", "Ongoing guidance"],
  },
  {
    id: "business-management",
    name: "Business Management",
    icon: ChartLineUp,
    price: "From $200/mo",
    desc: "We handle bookkeeping, payroll, compliance and financial oversight so you can focus on growth.",
    points: ["Basic, Growth & Executive plans", "Operations & team coordination", "Financial reporting"],
  },
  {
    id: "guaranteed-approval",
    name: "Guaranteed Approval",
    icon: Buildings,
    price: "From $350",
    desc: "Get placed into a home with guaranteed approval. Choose your property type and we handle the rest.",
    points: ["Houses & condos/apartments", "Fast, guided placement", "Simple info at purchase"],
  },
];

export default function Landing() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();

  const goStart = (serviceId) => {
    if (user && user.role !== "admin") navigate("/dashboard");
    else if (user?.role === "admin") navigate("/admin");
    else navigate("/register");
  };

  const dest = user ? (user.role === "admin" ? "/admin" : "/dashboard") : "/login";

  return (
    <div className="min-h-screen bg-background text-foreground">
      {/* Nav */}
      <header className="sticky top-0 z-50 border-b border-white/10 bg-zinc-950/60 backdrop-blur-xl">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="font-display text-xl tracking-tighter">DAFARGOS</span>
            <span className="overline text-primary mt-1">Holdings LLC</span>
          </div>
          <nav className="hidden md:flex items-center gap-8 text-sm text-muted-foreground">
            <a href="#services" className="hover:text-foreground transition-colors">Services</a>
            <a href="#how" className="hover:text-foreground transition-colors">How it works</a>
            <Link to="/tradelines" className="hover:text-foreground transition-colors" data-testid="nav-tradelines-link">Tradelines</Link>
            <Link to="/contact" className="hover:text-foreground transition-colors" data-testid="nav-contact-link">Contact</Link>
          </nav>
          <div className="flex items-center gap-3">
            {!loading && !user && (
              <>
                <Link to="/login" data-testid="nav-admin-link">
                  <Button variant="outline" size="sm" className="border-primary/40 text-primary hover:bg-primary/10">
                    Admin
                  </Button>
                </Link>
                <Link to="/login" data-testid="nav-login-link">
                  <Button variant="ghost" size="sm" className="text-muted-foreground hover:text-foreground">
                    Sign in
                  </Button>
                </Link>
              </>
            )}
            <Link to={dest} data-testid="nav-cta-button">
              <Button size="sm" className="bg-primary text-primary-foreground hover:bg-[#DBC197] active:scale-95 transition-transform">
                {user ? "Dashboard" : "Get Started"}
              </Button>
            </Link>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0">
          <img src={HERO} alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-zinc-950 via-zinc-950/85 to-zinc-950/30" />
          <div className="grain" />
        </div>
        <div className="relative max-w-7xl mx-auto px-6 py-32 md:py-44">
          <motion.div {...HERO_MOTION} className="max-w-2xl">
            <div className="overline text-primary mb-6 flex items-center gap-2">
              <ShieldCheck size={16} weight="fill" /> Trusted business partners
            </div>
            <h1 className="font-serif text-5xl md:text-6xl font-medium leading-[1.05] tracking-tight">
              We build your business, wealth, and future.
            </h1>
            <p className="mt-6 text-lg text-zinc-300 max-w-xl">
              DAFARGOS HOLDINGS LLC helps entrepreneurs form their company, build a
              strong business and personal financial foundation, and run day-to-day
              operations — all through simple, guided steps.
            </p>
            <div className="mt-10 flex flex-wrap gap-4">
              <Link to={dest} data-testid="hero-cta-button">
                <Button size="lg" className="bg-primary text-primary-foreground hover:bg-[#DBC197] active:scale-95 transition-transform">
                  Start your journey <ArrowRight size={18} className="ml-2" />
                </Button>
              </Link>
              <a href="#services">
                <Button size="lg" variant="outline" className="border-white/20 bg-white/5 hover:bg-white/10">
                  Explore services
                </Button>
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Services */}
      <section id="services" className="max-w-7xl mx-auto px-6 py-24 md:py-32">
        <div className="max-w-2xl mb-16">
          <div className="overline text-primary mb-4">What we do</div>
          <h2 className="font-serif text-4xl md:text-5xl font-medium tracking-tight">
            Everything you need. One trusted partner.
          </h2>
        </div>
        <div className="grid md:grid-cols-2 xl:grid-cols-4 gap-6">
          {services.map((s, i) => (
            <motion.div
              key={s.id}
              initial={CARD_INITIAL}
              whileInView={CARD_WHILE_INVIEW}
              viewport={CARD_VIEWPORT}
              transition={{ duration: 0.5, delay: i * 0.12 }}
              className="group relative rounded-xl border border-white/10 bg-zinc-900/50 p-8 hover:border-primary/40 transition-colors"
              data-testid={`service-card-${s.id}`}
            >
              <div className="flex items-start justify-between mb-6">
                <div className="h-12 w-12 rounded-lg bg-primary/10 flex items-center justify-center">
                  <s.icon size={26} weight="duotone" className="text-primary" />
                </div>
                <div className="font-display text-lg tracking-tighter text-primary">{s.price}</div>
              </div>
              <h3 className="font-display text-xl tracking-tight mb-3">{s.name}</h3>
              <p className="text-sm text-muted-foreground mb-6 leading-relaxed">{s.desc}</p>
              <ul className="space-y-2 mb-8">
                {s.points.map((p) => (
                  <li key={p} className="flex items-center gap-2 text-sm text-zinc-300">
                    <CheckCircle size={16} weight="fill" className="text-primary shrink-0" />
                    {p}
                  </li>
                ))}
              </ul>
              <button
                onClick={() => goStart(s.id)}
                data-testid={`service-start-${s.id}`}
                className="inline-flex items-center gap-2 text-sm font-medium text-primary hover:gap-3 transition-all"
              >
                Get started <ArrowRight size={16} />
              </button>
            </motion.div>
          ))}
        </div>
      </section>

      {/* How it works */}
      <section id="how" className="border-t border-white/10 bg-zinc-950">
        <div className="max-w-7xl mx-auto px-6 py-24 md:py-32">
          <div className="grid md:grid-cols-2 gap-16 items-center">
            <div>
              <div className="overline text-primary mb-4">How it works</div>
              <h2 className="font-serif text-4xl md:text-5xl font-medium tracking-tight mb-8">
                Simple guided steps, done for you.
              </h2>
              <div className="space-y-6">
                {[
                  ["Choose your service", "Pick business formation, credit building, or management."],
                  ["Fill out the template", "Answer our simple step-by-step questions at your own pace."],
                  ["We take it from here", "Our team reviews your info and gets to work."],
                ].map(([t, d], i) => (
                  <div key={t} className="flex gap-4">
                    <div className="h-9 w-9 shrink-0 rounded-full border border-primary/40 text-primary flex items-center justify-center font-display text-sm">
                      {i + 1}
                    </div>
                    <div>
                      <h4 className="font-medium mb-1">{t}</h4>
                      <p className="text-sm text-muted-foreground">{d}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <img src={FORMATION_IMG} alt="" className="rounded-xl border border-white/10 h-full object-cover" />
              <img src={MGMT_IMG} alt="" className="rounded-xl border border-white/10 h-full object-cover mt-8" />
            </div>
          </div>
        </div>
      </section>

      <section className="border-t border-white/10">
        <div className="max-w-6xl mx-auto px-6 py-20">
          <div className="overline text-primary">Client resources</div>
          <h2 className="font-serif text-3xl md:text-4xl font-medium tracking-tight mt-2">Sign up &amp; submit your info</h2>
          <p className="text-muted-foreground mt-3 max-w-2xl">Use our partner links to get started. After you sign up for Credit Hero, submit your login and credit report so our team can begin working your file.</p>
          <div className="grid sm:grid-cols-2 gap-5 mt-8">
            <div className="rounded-xl border border-white/10 bg-zinc-900/50 p-6">
              <div className="font-medium text-lg">Credit Hero</div>
              <p className="text-sm text-muted-foreground mt-1">Create your credit-monitoring account through our partner link.</p>
              <div className="flex flex-wrap gap-3 mt-5">
                <a href="https://creditheroscore.com/redirect.asp?guid=IK42ZBH6B828&sid=ZSFHSQHP2" target="_blank" rel="noreferrer" data-testid="landing-credithero-signup" className="inline-flex items-center rounded-full bg-primary text-primary-foreground px-5 py-2.5 text-sm font-medium hover:bg-[#DBC197] transition-colors">Sign up on Credit Hero →</a>
                <Link to="/forms/credit-hero" data-testid="landing-credithero-submit" className="inline-flex items-center rounded-full border border-white/15 px-5 py-2.5 text-sm font-medium hover:bg-white/5 transition-colors">Submit my info</Link>
              </div>
            </div>
            <div className="rounded-xl border border-white/10 bg-zinc-900/50 p-6">
              <div className="font-medium text-lg">Google Workspace</div>
              <p className="text-sm text-muted-foreground mt-1">Set up a professional business email &amp; workspace.</p>
              <div className="mt-5">
                <a href="https://c.gle/ACT4xYxm48X7784IpJ4E7JAyI8Ed6gRAxxD9CPsk4kFNOkctYuS7EO_81NFIkvj8braYC8x25_Pa9kTaecGhNjiBWjzNL54toilK4X_rKY6P2MxcXkYFLMX397dwlKFzLbBmLrc8S5FNAjGiUiPwc3fP" target="_blank" rel="noreferrer" data-testid="landing-workspace" className="inline-flex items-center rounded-full border border-white/15 px-5 py-2.5 text-sm font-medium hover:bg-white/5 transition-colors">Get Google Workspace →</a>
              </div>
            </div>
          </div>
        </div>
      </section>

      <footer className="border-t border-white/10">
        <div className="max-w-7xl mx-auto px-6 py-10 flex flex-col sm:flex-row items-center justify-between gap-4 text-sm text-muted-foreground">
          <span className="font-display tracking-tighter text-foreground">DAFARGOS HOLDINGS LLC</span>
          <span>© {new Date().getFullYear()} DAFARGOS HOLDINGS LLC. All rights reserved.</span>
        </div>
      </footer>
    </div>
  );
}
